import * as React from 'react';

import './ResourceAccessBlock.css';

type ResourceAccessBlockProps = React.HTMLAttributes<HTMLDivElement> & {
  title?: string;
  subtitle?: string;
  image: React.ReactNode;
  formHeading?: string;
  formDescription?: string;
  formContent: React.ReactNode;
  successContent?: React.ReactNode;
  submissionSuccess?: boolean;
  /** Where the block's children sit relative to the image. Defaults to below, as every
   *  resource page expects; 'above-image' puts intro copy ahead of the image so it is
   *  read before the reader reaches the form. */
  childrenPlacement?: 'below-image' | 'above-image';
};

export const ResourceAccessBlock = ({
  title,
  subtitle,
  image,
  formHeading = 'Download now',
  formDescription = 'Fill out the form to get free access to this document.',
  formContent,
  successContent,
  submissionSuccess = false,
  childrenPlacement = 'below-image',
  children,
  className = '',
  ...props
}: React.PropsWithChildren<ResourceAccessBlockProps>) => {
  const childrenBlock = children ? (
    <div className="resource-access-block__children">{children}</div>
  ) : null;

  // When the form swaps to the success panel the page height can collapse,
  // stranding the user scrolled at the footer — and the swap is silent for
  // screen readers. On the false→true transition, scroll the success panel
  // into view and move focus to it (role="status" announces the content).
  const successRef = React.useRef<HTMLDivElement>(null);
  const wasSuccessRef = React.useRef(submissionSuccess);
  React.useEffect(() => {
    if (submissionSuccess && !wasSuccessRef.current && successRef.current) {
      const panel = successRef.current;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      panel.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      panel.focus({ preventScroll: true });
    }
    wasSuccessRef.current = submissionSuccess;
  }, [submissionSuccess]);

  return (
    <div className={`resource-access-block ${className}`} {...props}>
      <div className="resource-access-block__content">
        <div className="resource-access-block__grid">
          <div
            className={`resource-access-block__left${
              childrenPlacement === 'above-image' ? ' --children-above-image' : ''
            }`}
          >
            {title && <h4>{title}</h4>}
            {subtitle && <p className="--feature_heading">{subtitle}</p>}
            {childrenPlacement === 'above-image' && childrenBlock}
            <div className="resource-access-block__image">{image}</div>
            {childrenPlacement === 'below-image' && childrenBlock}
          </div>

          <div className="resource-access-block__right">
            {!submissionSuccess && (
              <>
                {formHeading && <h6>{formHeading}</h6>}
                {formDescription && (
                  <div className="resource-access-block__description">
                    <p>{formDescription}</p>
                  </div>
                )}
                <div className="resource-access-block__form">{formContent}</div>
              </>
            )}

            {submissionSuccess && successContent && (
              <div
                className="resource-access-block__success"
                ref={successRef}
                tabIndex={-1}
                role="status"
              >
                {successContent}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
